import { useDataProvider } from "@exonn/base";
import {
  type DefaultError,
  type QueryKey,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  GridFilterModel,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useResourceQueries } from "@exonn/base";
import type { ValidGetListResponse } from "@exonn/base";
import { logger } from "src/logger";
import { useGlobalSettings } from "src/hooks/use-global-settings.ts";
import { usePrevious } from "react-use";
import { useEffect } from "react";
import { deepEq } from "@exonn/utils";

type UseListProps<Row extends GridValidRowModel = GridValidRowModel> = {
  resource: string;
  id?: string;
  filter?: GridFilterModel;
  filter2?: GridFilterModel;
  groupKeys?: string[];
  include?: string[];
  refreshId?: number | string;
  sort?: GridSortModel;
  onQueryFn?: () => void;
  page?: {
    size: number;
    number: number;
  };
  withAggregation?: boolean;
  disablePagination?: boolean;
  invalidatePreviousQuery?: boolean;
  useSimplePagination?: boolean;
  locale?: string;
  queryOptions?: Omit<
    UseQueryOptions<
      ValidGetListResponse<Row>,
      DefaultError,
      ValidGetListResponse<Row>,
      any
    >,
    "queryKey" | "queryFn"
  >;
};

export const useList = <Row extends GridValidRowModel = GridValidRowModel>(
  props: UseListProps<Row>,
): {
  query: UseQueryResult<ValidGetListResponse<Row>, DefaultError>;
  queryKey: QueryKey;
} => {
  const {
    resource,
    id,
    filter,
    filter2,
    sort,
    page: pageProp,
    locale: localeProp,
    withAggregation,
    groupKeys,
    refreshId,
    useSimplePagination = false,
    include,
    onQueryFn,
    queryOptions,
    invalidatePreviousQuery,
    disablePagination,
  } = props;
  const { getList } = useDataProvider();

  const {
    data: { default_language },
  } = useGlobalSettings();

  const defaultLocale = default_language?.locale;
  const queries = useResourceQueries();
  const locale = localeProp || defaultLocale || "de";
  const page = disablePagination
    ? {
        number: 1,
        size: 999999,
      }
    : pageProp;

  const meta = {
    include,
    refreshId,
    withAggregation,
    useSimplePagination,
    filter2,
    locale,
    id,
    groupKeys,
  };

  const listParams = {
    filter,
    sort,
    page,
    resource,
    meta,
  };
  const queryKey = queries.list(listParams);

  const queryClient = useQueryClient();

  const previousQueryKey = usePrevious(queryKey);

  useEffect(() => {
    if (
      invalidatePreviousQuery &&
      previousQueryKey &&
      !deepEq(previousQueryKey, queryKey)
    ) {
      queryClient.invalidateQueries({
        queryKey: previousQueryKey,
      });
    }
  }, [previousQueryKey, queryKey, invalidatePreviousQuery, queryClient]);

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        onQueryFn?.();
        const params = {
          ...listParams,
          signal,
        };
        return (await getList(params)) as ValidGetListResponse<Row>;
      } catch (e: any) {
        logger.error(e);
        throw e;
      }
    },
    ...queryOptions,
  }) as UseQueryResult<ValidGetListResponse<Row>, DefaultError>;

  return {
    query,
    queryKey,
  };
};
